import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CalendarIcon, MapPin, Clock, User, X, Calendar as CalendarCheckIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function ManageAppointment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<any>(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState<Date>();
  const [newStartTime, setNewStartTime] = useState("09:00");
  const [newEndTime, setNewEndTime] = useState("10:00");
  const [cancellationReason, setCancellationReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (token) {
      loadAppointment();
    }
  }, [token]);

  const loadAppointment = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-appointment', {
        method: 'GET',
        body: { token },
      });

      if (error) throw error;

      if (data?.appointment) {
        setAppointment(data.appointment);
        const startTime = new Date(data.appointment.start_time);
        setNewDate(startTime);
        setNewStartTime(format(startTime, "HH:mm"));
        const endTime = new Date(data.appointment.end_time);
        setNewEndTime(format(endTime, "HH:mm"));
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
      toast({
        title: "Error",
        description: "Could not load appointment. The link may be invalid or expired.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!token) return;
    
    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-appointment', {
        method: 'POST',
        body: {
          token,
          action: 'cancel',
          reason: cancellationReason,
        },
      });

      if (error) throw error;

      toast({
        title: "Appointment Cancelled",
        description: "Your appointment has been cancelled successfully.",
      });

      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: "Error",
        description: "Could not cancel appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReschedule = async () => {
    if (!token || !newDate) return;

    setProcessing(true);
    try {
      const [startHour, startMin] = newStartTime.split(":").map(Number);
      const [endHour, endMin] = newEndTime.split(":").map(Number);
      
      const newStartDateTime = new Date(newDate);
      newStartDateTime.setHours(startHour, startMin, 0, 0);
      
      const newEndDateTime = new Date(newDate);
      newEndDateTime.setHours(endHour, endMin, 0, 0);

      const { data, error } = await supabase.functions.invoke('manage-appointment', {
        method: 'POST',
        body: {
          token,
          action: 'reschedule',
          newStartTime: newStartDateTime.toISOString(),
          newEndTime: newEndDateTime.toISOString(),
        },
      });

      if (error) throw error;

      toast({
        title: "Appointment Rescheduled",
        description: "Your appointment has been rescheduled successfully.",
      });

      setShowReschedule(false);
      await loadAppointment();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast({
        title: "Error",
        description: "Could not reschedule appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading appointment...</p>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Appointment Not Found</CardTitle>
            <CardDescription>
              This appointment link is invalid or has expired.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (appointment.status === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Appointment Cancelled</CardTitle>
            <CardDescription>
              This appointment has already been cancelled.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              If you need to schedule a new appointment, please contact us.
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startTime = new Date(appointment.start_time);
  const endTime = new Date(appointment.end_time);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4 py-12">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Manage Your Appointment</h1>
          <p className="text-muted-foreground">
            Cancel or reschedule your appointment below
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarCheckIcon className="h-5 w-5" />
              {appointment.title}
            </CardTitle>
            <CardDescription>
              {appointment.appointment_type && (
                <span className="capitalize">{appointment.appointment_type}</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{appointment.crm_contact?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {appointment.crm_contact?.phone_number}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{format(startTime, "EEEE, MMMM d, yyyy")}</p>
                <p className="text-sm text-muted-foreground">
                  {format(startTime, "h:mm a")} - {format(endTime, "h:mm a")}
                </p>
              </div>
            </div>

            {appointment.location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <p className="text-sm">{appointment.location}</p>
              </div>
            )}

            {appointment.description && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">{appointment.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {!showReschedule ? (
          <div className="grid gap-3 md:grid-cols-2">
            <Button
              onClick={() => setShowReschedule(true)}
              variant="outline"
              size="lg"
              className="w-full"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Reschedule Appointment
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="lg" className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Cancel Appointment
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Appointment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to cancel this appointment? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="my-4">
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Let us know why you're cancelling..."
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    rows={3}
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancel}
                    disabled={processing}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {processing ? 'Cancelling...' : 'Yes, Cancel'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Reschedule Appointment</CardTitle>
              <CardDescription>Select a new date and time</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>New Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !newDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newDate ? format(newDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newDate}
                      onSelect={setNewDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="newStartTime">Start Time</Label>
                  <Input
                    id="newStartTime"
                    type="time"
                    value={newStartTime}
                    onChange={(e) => setNewStartTime(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newEndTime">End Time</Label>
                  <Input
                    id="newEndTime"
                    type="time"
                    value={newEndTime}
                    onChange={(e) => setNewEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowReschedule(false)}
                  className="flex-1"
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReschedule}
                  className="flex-1"
                  disabled={processing || !newDate}
                >
                  {processing ? 'Rescheduling...' : 'Confirm Reschedule'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
